// ============================================================================
// KLINISOL CRO - Master SOP Register Component
// Professional registry view of all SOPs grouped by category
// COLOR SCHEME: Slate Grey (#475569) & Magenta (#E91E63)
// ============================================================================

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
    FileText, 
    ChevronDown, 
    ChevronRight,
    Search,
    Download,
    Printer,
    Eye,
    Calendar,
    User,
    Loader2,
    AlertCircle,
    ArrowLeft,
    FileStack,
    Shield
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import SOPViewer from '../components/SOPViewer';

const MasterSOPRegister = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    
    const [sops, setSops] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [expandedCategories, setExpandedCategories] = useState({});
    const [selectedSop, setSelectedSop] = useState(null);

    useEffect(() => {
        loadData();
    }, []);

    // Listen for openSOP events from enhanced viewer (for clickable Master Register links)
    useEffect(() => {
        const handleOpenSOP = (event) => {
            const { id, sop_number, file_type, fromMasterRegister, masterRegisterId } = event.detail;
            const sopToOpen = { 
                id, 
                sop_number, 
                file_type,
                fromMasterRegister: fromMasterRegister || false,
                masterRegisterId
            };
            handleViewSop(sopToOpen);
        };

        window.addEventListener('openSOP', handleOpenSOP);
        
        return () => {
            window.removeEventListener('openSOP', handleOpenSOP);
        };
    }, []);

    const loadData = async () => {
        setLoading(true);
        setError('');
        
        try {
            const [sopsResponse, categoriesResponse] = await Promise.all([
                api.get('/sops'),
                api.get('/sops/categories')
            ]);
            
            setSops(sopsResponse.data.data || []);
            setCategories(categoriesResponse.data.data || []);
            
            // Expand all categories by default
            const expanded = {};
            categoriesResponse.data.data.forEach(cat => {
                expanded[cat.category] = true;
            });
            setExpandedCategories(expanded);
            
        } catch (err) {
            console.error('Error loading SOPs:', err);
            setError('Failed to load SOP register. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const toggleCategory = (category) => {
        setExpandedCategories(prev => ({
            ...prev,
            [category]: !prev[category]
        }));
    };

    const handleViewSop = (sop) => {
        setSelectedSop(sop);
    };

    const handlePrint = () => {
        window.print();
    };

    const handleExport = () => {
        // Create CSV content
        const headers = ['SOP Number', 'Title', 'Category', 'Version', 'Effective Date', 'Review Date', 'Owner', 'Status'];
        const rows = filteredSops.map(sop => [
            sop.sop_number,
            sop.title,
            sop.category,
            sop.version,
            new Date(sop.effective_date + 'T00:00:00Z').toLocaleDateString('en-US', {timeZone: 'UTC'}),
            new Date(sop.review_date + 'T00:00:00Z').toLocaleDateString('en-US', {timeZone: 'UTC'}),
            sop.owner,
            sop.status
        ]);
        
        const csvContent = [
            headers.join(','),
            ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
        ].join('\n');
        
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `KLINISOL_Master_SOP_Register_${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    // Group SOPs by category
    const sopsByCategory = categories.reduce((acc, cat) => {
        acc[cat.category] = sops.filter(sop => sop.category === cat.category);
        return acc;
    }, {});

    // Filter SOPs based on search
    const filteredSops = sops.filter(sop => 
        searchTerm === '' || 
        sop.sop_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
        sop.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        sop.category.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const filteredSopsByCategory = categories.reduce((acc, cat) => {
        acc[cat.category] = filteredSops.filter(sop => sop.category === cat.category);
        return acc;
    }, {});

    if (loading) {
        return (
            <div className="min-h-screen bg-[#f8fafd] flex items-center justify-center">
                <div className="text-center">
                    <Loader2 className="w-12 h-12 animate-spin text-[#475569] mx-auto mb-4" />
                    <p className="text-gray-600">Loading Master SOP Register...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#f8fafd]">
            {/* Header - Print Hidden */}
            <div className="bg-gradient-to-r from-[#475569] via-[#E91E63] to-[#475569] text-white py-8 px-6 shadow-md print:hidden">
                <div className="max-w-7xl mx-auto">
                    <button
                        onClick={() => navigate('/sop-library')}
                        className="flex items-center gap-2 text-white/80 hover:text-white mb-4 transition-colors"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        Back to SOP Library
                    </button>
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                                <FileStack className="w-8 h-8 text-white" />
                            </div>
                            <div>
                                <h1 className="text-3xl font-bold mb-1">Master SOP Register</h1>
                                <p className="text-sm opacity-90">
                                    KLINISOL Contract Research Organization - Document Control Index
                                </p>
                            </div>
                        </div>
                        <div className="text-right">
                            <p className="text-sm opacity-80">Total SOPs</p>
                            <p className="text-4xl font-bold">{sops.length}</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Print Header - Only visible when printing */}
            <div className="hidden print:block border-b-4 border-[#475569] pb-4 mb-6">
                <div className="text-center">
                    <h1 className="text-3xl font-bold text-[#475569] mb-2">
                        KLIN<span className="text-[#E91E63]">i</span>SOL CRO
                    </h1>
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">Master SOP Register</h2>
                    <p className="text-sm text-gray-600">
                        Document Control Index - Generated: {new Date().toLocaleDateString()}
                    </p>
                    <p className="text-sm text-gray-600">Total SOPs: {sops.length}</p>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-6 py-8">
                {/* Regulatory Notice */}
                <div className="bg-blue-50 border-2 border-blue-500 rounded-xl p-6 mb-6 print:hidden">
                    <div className="flex items-start gap-3">
                        <Shield className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
                        <div>
                            <h3 className="text-lg font-bold text-blue-900 mb-2">Regulatory Record</h3>
                            <p className="text-sm text-blue-800">
                                This dynamic register is for operational use and provides real-time access to all SOPs. 
                                The official, electronically signed <strong>Master SOP Register (OPS-Index-001 v1.5)</strong> is 
                                available in the <button onClick={() => navigate('/sop-library')} className="text-blue-600 underline hover:text-blue-800 font-semibold">SOP Library</button> and 
                                serves as the controlled regulatory record for FDA compliance and audit purposes.
                            </p>
                        </div>
                    </div>
                </div>

                {/* Error Message */}
                {error && (
                    <div className="bg-red-50 border-2 border-red-500 text-red-900 px-6 py-4 rounded-xl mb-6 flex items-start gap-3">
                        <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                        <p>{error}</p>
                    </div>
                )}

                {/* Controls - Print Hidden */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-6 print:hidden">
                    <div className="flex flex-wrap items-center justify-between gap-4">
                        {/* Search */}
                        <div className="flex-1 min-w-[300px]">
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                                <input
                                    type="text"
                                    placeholder="Search by SOP number, title, or category..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-[#475569] transition-all"
                                />
                            </div>
                        </div>
                        
                        {/* Action Buttons */}
                        <div className="flex items-center gap-3">
                            <button
                                onClick={handlePrint}
                                className="px-4 py-3 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition-all flex items-center gap-2"
                            >
                                <Printer className="w-5 h-5" />
                                Print
                            </button>
                            <button
                                onClick={handleExport}
                                className="px-4 py-3 bg-gradient-to-r from-[#475569] to-[#334155] text-white rounded-xl font-semibold hover:from-[#334155] hover:to-[#1e293b] transition-all flex items-center gap-2"
                            >
                                <Download className="w-5 h-5" />
                                Export CSV
                            </button>
                        </div>
                    </div>
                </div>

                {/* SOP Register Table */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                    {categories.map((category) => {
                        const categorySOPs = filteredSopsByCategory[category.category] || [];
                        
                        if (categorySOPs.length === 0 && searchTerm) return null;
                        
                        return (
                            <div key={category.category} className="border-b border-gray-200 last:border-b-0">
                                {/* Category Header */}
                                <button
                                    onClick={() => toggleCategory(category.category)}
                                    className="w-full bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-4 flex items-center justify-between hover:from-gray-100 hover:to-gray-200 transition-all print:bg-gray-100"
                                >
                                    <div className="flex items-center gap-3">
                                        {expandedCategories[category.category] ? (
                                            <ChevronDown className="w-5 h-5 text-[#475569] print:hidden" />
                                        ) : (
                                            <ChevronRight className="w-5 h-5 text-[#475569] print:hidden" />
                                        )}
                                        <h3 className="text-lg font-bold text-[#475569]">
                                            {category.category}
                                        </h3>
                                        <span className="px-3 py-1 bg-[#475569] text-white rounded-full text-xs font-bold">
                                            {categorySOPs.length} SOPs
                                        </span>
                                    </div>
                                    <p className="text-sm text-gray-600 italic hidden md:block">
                                        {category.description}
                                    </p>
                                </button>
                                
                                {/* Category SOPs Table */}
                                {(expandedCategories[category.category] || searchTerm) && categorySOPs.length > 0 && (
                                    <div className="overflow-x-auto">
                                        <table className="w-full">
                                            <thead className="bg-gray-50 border-b-2 border-gray-200">
                                                <tr>
                                                    <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                                                        SOP Number
                                                    </th>
                                                    <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                                                        Title
                                                    </th>
                                                    <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                                                        Version
                                                    </th>
                                                    <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                                                        Effective Date
                                                    </th>
                                                    <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                                                        Owner
                                                    </th>
                                                    <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider print:hidden">
                                                        Actions
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-200">
                                                {categorySOPs.map((sop) => (
                                                    <tr 
                                                        key={sop.id}
                                                        className="hover:bg-gray-50 transition-colors"
                                                    >
                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                            <span className="font-mono font-bold text-[#475569]">
                                                                {sop.sop_number}
                                                            </span>
                                                        </td>
                                                        <td className="px-6 py-4">
                                                            <span className="text-sm text-gray-900">
                                                                {sop.title}
                                                            </span>
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                            <span className="text-sm font-semibold text-gray-700">
                                                                {sop.version}
                                                            </span>
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                            <div className="flex items-center gap-2 text-sm text-gray-600">
                                                                <Calendar className="w-4 h-4" />
                                                                {new Date(sop.effective_date + 'T00:00:00Z').toLocaleDateString('en-US', {timeZone: 'UTC'})}
                                                            </div>
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                            <div className="flex items-center gap-2 text-sm text-gray-600">
                                                                <User className="w-4 h-4" />
                                                                {sop.owner}
                                                            </div>
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap print:hidden">
                                                            <button
                                                                onClick={() => handleViewSop(sop)}
                                                                className="inline-flex items-center gap-2 px-3 py-2 bg-[#475569] text-white rounded-lg text-sm font-semibold hover:bg-[#334155] transition-all"
                                                            >
                                                                <Eye className="w-4 h-4" />
                                                                View
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>

                {/* Empty State */}
                {filteredSops.length === 0 && searchTerm && (
                    <div className="text-center py-12 text-gray-500">
                        <FileText className="w-16 h-16 mx-auto mb-4 opacity-50" />
                        <p className="text-lg font-semibold mb-2">No SOPs found</p>
                        <p className="text-sm">Try adjusting your search criteria</p>
                    </div>
                )}

                {/* Footer Info - Print Only */}
                <div className="hidden print:block mt-8 pt-4 border-t-2 border-gray-200">
                    <div className="text-xs text-gray-600 text-center">
                        <p className="mb-1">This document is controlled by KLINISOL CRO Document Control System</p>
                        <p>Printed copies are uncontrolled - Verify current version before use</p>
                        <p className="mt-2">Generated by: {user?.firstName} {user?.lastName} on {new Date().toLocaleString()}</p>
                    </div>
                </div>
            </div>

            {/* SOP Viewer Modal */}
            {selectedSop && (
                <SOPViewer
                    sopId={selectedSop.id}
                    sopNumber={selectedSop.sop_number}
                    fileType={selectedSop.file_type}
                    fromMasterRegister={selectedSop.fromMasterRegister || false}
                    onClose={() => setSelectedSop(null)}
                />
            )}

            {/* Print Styles */}
            <style>{`
                @media print {
                    body {
                        print-color-adjust: exact;
                        -webkit-print-color-adjust: exact;
                    }
                    
                    .print\\:hidden {
                        display: none !important;
                    }
                    
                    .print\\:block {
                        display: block !important;
                    }
                    
                    .print\\:bg-gray-100 {
                        background-color: #f3f4f6 !important;
                    }
                    
                    table {
                        page-break-inside: auto;
                    }
                    
                    tr {
                        page-break-inside: avoid;
                        page-break-after: auto;
                    }
                    
                    thead {
                        display: table-header-group;
                    }
                }
            `}</style>
        </div>
    );
};

export default MasterSOPRegister;
